import { CURRENT_ENV, FLAVOUR, IS_DEV, IS_STAGING } from 'env';

export const LINE_BREAK_REGEX = /(\r\n|\n|\r)/gm;

/**
 * Environment label used to prefix exported filenames. Only staging and local
 * get a prefix, everything else (prod, preprod, presentation, etc.)
 * keeps the bare filename.
 */
const getExportFilenamePrefix = (): string => {
    if (IS_DEV) return 'local_';
    if (!IS_STAGING) return '';
    const label = FLAVOUR || CURRENT_ENV;
    return label ? `${label}_` : '';
};

/**
 * Prefix a filename with the current environment.
 */
export const prependEnvToFilename = (filename: string): string =>
    `${getExportFilenamePrefix()}${filename}`;

/**
 * Install a global interceptor that prefixes the `download` filename of every
 * anchor-based download with the current environment. The anchor must be
 * attached to the document when clicked so the event reaches this listener
 * (both downloadFile and downloadCSV append it for that reason).
 *
 * Exception: a download triggered from a *detached* anchor (e.g. file-saver,
 * which dispatches the click on an unattached element) never reaches
 * `document`. Those call sites must call `prependEnvToFilename` explicitly.
 *
 * No-op on prod, so production downloads are untouched.
 */
export const installExportFilenameInterceptor = (): void => {
    const prefix = getExportFilenamePrefix();
    if (!prefix) {
        return;
    }
    document.addEventListener(
        'click',
        (event) => {
            const anchor = event
                .composedPath()
                .find(
                    (target): target is HTMLAnchorElement =>
                        target instanceof HTMLAnchorElement &&
                        target.hasAttribute('download'),
                );
            if (
                !anchor ||
                !anchor.download ||
                anchor.download.startsWith(prefix)
            ) {
                return;
            }
            anchor.download = prependEnvToFilename(anchor.download);
        },
        true,
    );
};

// OWASP CSV injection list, full-width ＝ ＋ － ＠ included as no amount uses them
const ALWAYS_ESCAPED_FORMULA_TRIGGERS = [
    '=',
    '@',
    '\t',
    '\r',
    '\n',
    '\uff1d',
    '\uff0b',
    '\uff0d',
    '\uff20',
];
const SIGN_FORMULA_TRIGGERS = ['+', '-'];
// A sign followed only by digits, separators, spaces, % or € is a plain number
// (-12,50, -1 234,56 €, +33 6 12 34 56 78) that cannot call a function
const NUMBER_LIKE_REMAINDER_REGEX = /^[\d\s.,%€]*$/;

const isFormulaLike = (value: string): boolean => {
    const firstChar = value.charAt(0);
    if (ALWAYS_ESCAPED_FORMULA_TRIGGERS.includes(firstChar)) return true;
    if (SIGN_FORMULA_TRIGGERS.includes(firstChar))
        return !NUMBER_LIKE_REMAINDER_REGEX.test(value.slice(1));
    return false;
};

/**
 * Prefix a formula-like CSV cell with an apostrophe so spreadsheet software
 * reads it as text instead of evaluating it (CSV injection, CWE-1236).
 * Mirrors _escape_csv_cell in vision-back (common/helpers/spreadsheet_export_helpers.py).
 */
export const escapeCsvFormula = (value: string): string =>
    isFormulaLike(value) ? `'${value}` : value;

type CSVDataPoint<HeaderT extends string> = Record<
    HeaderT,
    string | number | Date
>;
type CSVData<HeaderT extends string> = CSVDataPoint<HeaderT>[];

export const dataToCSVString = <HeaderT extends string>(
    data: CSVData<HeaderT>,
    headers: readonly HeaderT[],
    cellPreProcess:
        | ((cellStr: string, headerKey: string) => string)
        | null = null,
) => {
    const separator = ';';
    const separatorRegex = /;/g;

    // we start with utf8 BOM prefixe to avoid encoding errors
    // https://stackoverflow.com/questions/17879198/adding-utf-8-bom-to-string-blob
    const utf8BOM = '\ufeff';

    let csvStr = `${utf8BOM}${headers.join(separator)}\n`;

    const serializeCell = (row: CSVDataPoint<HeaderT>, headerKey: HeaderT) => {
        const cellValue = headerKey in row ? row[headerKey] : ' ';
        const cellStr =
            cellPreProcess !== null
                ? cellPreProcess(cellValue.toString(), headerKey)
                : cellValue.toString();

        // we remove multiline values, double quotes and separators in the cell value
        return escapeCsvFormula(
            cellStr
                .replace(LINE_BREAK_REGEX, ' ')
                .replace(separatorRegex, ' ')
                .replace(/"/g, ' '),
        );
    };

    data.forEach((row) => {
        const csvRowValues = headers.map((header) =>
            serializeCell(row, header),
        );
        csvStr += `${csvRowValues.join(separator)}\n`;
    });
    return csvStr;
};

/**
 * Generate a CSV file from a string and download it
 * @param csvStr the CSV content
 * @param title the file title
 */
export const downloadCSV = (csvStr: string, title: string): void => {
    const blob = new Blob([csvStr], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const hiddenElement = document.createElement('a');
    hiddenElement.href = url;
    hiddenElement.target = '_blank';
    hiddenElement.download = `${title}.csv`;
    document.body.appendChild(hiddenElement);
    hiddenElement.click();
    document.body.removeChild(hiddenElement);

    // Revoke the blob URL to release memory
    URL.revokeObjectURL(url);
};
